import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowRight, Heart } from 'lucide-react';
import { FaInstagram, FaLinkedin } from 'react-icons/fa';
import { motion } from 'framer-motion';

const Footer = () => {
  // For links that should go to the top of the page
  const handleClickTop = () => {
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
  };

  // For links that should scroll to a section on the home page
  const handleSectionClick = (e, sectionId) => {
    const isHomepage = window.location.pathname === "/" || window.location.pathname === "";
    
    if (isHomepage) {
      e.preventDefault();
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      // If not on homepage, store the section ID to scroll to after navigation
      sessionStorage.setItem("scrollToSection", sectionId);
    }
  };

  const currentYear = new Date().getFullYear();

  // Updated quick links to match navbar structure
  const quickLinks = [
    { name: 'Home', path: '/', scrollToTop: true },
    { name: 'About', path: '/#vision', sectionId: 'vision' },
    { name: 'Team', path: '/#team', sectionId: 'team' },
    { name: 'What We Do', path: '/#what-we-do', sectionId: 'what-we-do' },
    { name: 'Get Involved', path: '/volunteer/calendar', scrollToTop: true }
  ];

  return (
    <footer className="relative overflow-hidden bg-secondary text-white">
      {/* Decorative background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -left-40 w-80 h-80 rounded-full opacity-10 bg-primary"></div>
        <div className="absolute -bottom-40 -right-40 w-80 h-80 rounded-full opacity-10 bg-cta"></div>
      </div>

      {/* Main footer content */}
      <div className="relative max-w-6xl mx-auto px-6 md:px-8 pt-16 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-16">
          {/* Column 1: Organization Info */}
          <div>
            <h3 className="font-display text-h3 text-white mb-6">Students for Refugees</h3>
            <p className="text-white opacity-80 mb-6">
              Empowering displaced youth through education, mentorship, and advocacy, creating pathways to success for refugee students worldwide.
            </p>
            <div className="flex space-x-4">
              <motion.a 
                href="mailto:hello@studentsforrefugees.org"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center"
                whileHover={{ y: -3, backgroundColor: 'rgba(255,255,255,0.2)' }}
                transition={{ duration: 0.2 }}
                aria-label="Email"
              >
                <Mail size={18} />
              </motion.a>
              <motion.a 
                href="https://www.instagram.com/sfrnational/"
                target="_blank"
                rel="noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center"
                whileHover={{ y: -3, backgroundColor: 'rgba(255,255,255,0.2)' }}
                transition={{ duration: 0.2 }}
                aria-label="Instagram"
              >
                <FaInstagram size={18} />
              </motion.a>
              <motion.a 
                href="https://www.linkedin.com/company/studentsforrefugees"
                target="_blank"
                rel="noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center"
                whileHover={{ y: -3, backgroundColor: 'rgba(255,255,255,0.2)' }}
                transition={{ duration: 0.2 }}
                aria-label="LinkedIn"
              >
                <FaLinkedin size={18} />
              </motion.a>
            </div>
          </div>

          {/* Column 2: Quick Links */}
          <div>
            <h3 className="font-display text-h3 text-white mb-6">Quick Links</h3>
            <ul className="space-y-3">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <Link 
                    to={link.path} 
                    onClick={(e) => {
                      if (link.scrollToTop) {
                        handleClickTop();
                      } else if (link.sectionId) {
                        handleSectionClick(e, link.sectionId);
                      }
                    }}
                    className="text-white opacity-80 hover:opacity-100 hover:text-cta transition-colors duration-300 flex items-center"
                  >
                    <ArrowRight size={16} className="mr-2" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3: Contact Info */}
          <div>
            <h3 className="font-display text-h3 text-white mb-6">Contact Us</h3>
            <p className="flex items-start mb-4">
              <Mail className="mr-3 mt-1 flex-shrink-0" size={18} />
              <span className="text-white opacity-80">
                hello@studentsforrefugees.org
              </span>
            </p>
            <p className="text-white opacity-80 mb-4">
              Texas, USA
            </p>
            
            <a 
              href="https://linktr.ee/studentsforrefugees" 
              target="_blank" 
              rel="noreferrer"
              className="inline-flex items-center text-cta hover:underline"
            >
              Visit our Linktree
              <ArrowRight size={16} className="ml-2" />
            </a>
          </div>

          {/* Column 4: Quote */}
          <div>
            <h3 className="font-display text-h3 text-white mb-6">Our Inspiration</h3>
            <blockquote className="border-l-4 border-cta pl-4 italic text-white opacity-80">
              "If you want others to be happy, practice compassion. If you want to be happy, practice compassion."
              <footer className="mt-2 text-sm">
                — H.H Dalai Lama
              </footer>
            </blockquote>
            
            <div className="mt-6">
              <Link 
                to="/donate" 
                // to="/#donation" 
                // onClick={(e) => handleSectionClick(e, '/donate')}
                onClick={(e) => handleClickTop()}
                className="inline-flex items-center bg-cta text-white py-3 px-6 rounded-lg hover:bg-cta/90 transition-colors duration-300"
              >
                <Heart size={18} className="mr-2" />
                Donate Now
              </Link>
            </div>
          </div>
        </div>
        
        {/* Footer bottom - Copyright */}
        <div className="pt-8 border-t border-white/10">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-white opacity-60 text-sm">
              &copy; {currentYear} Students for Refugees. All rights reserved.
            </p>
            <p className="text-white opacity-60 text-sm mt-2 md:mt-0">
              501(c)(3) Non-Profit Organization | Est. 2019
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;